import { useRef, useState, useEffect, useCallback } from "react";
import { CoreSetup, ArcwareConfig, ArcwarePixelStreaming } from "@arcware-cloud/pixelstreaming-websdk";

// ─────────────────────────────────────────────────────────────────────────────
// Arcware stream adapter
//
// Wraps the Arcware Cloud WebSDK (@arcware-cloud/pixelstreaming-websdk) behind the
// same imperative surface the emulator used with the old Interlucent <pixel-stream>
// element: { play, cancel, stop, sendMessage, status, containerRef }. Only the
// transport changes; the rest of the emulator stays the same.
//
// We use CoreSetup (not ArcwareInit) on purpose: CoreSetup returns the bare
// PixelStreaming core WITHOUT Arcware/Epic's own UI overlays (click-to-start, AFK,
// buttons), so our custom idle/pending/ready/error overlay stays the single source
// of UI. AutoConnect is false so the user still clicks the "▶" idle overlay to start
// (signup/viewer modes call play()).
//
// There is no reflected `status` attribute like Interlucent had, so we DERIVE a
// status string (idle | connecting | queued | streaming | ended | failed) from the
// SDK's event handlers, reusing the vocabulary the emulator's timers/overlays expect.
// ─────────────────────────────────────────────────────────────────────────────

// Escalating recovery for a UE freeze-frame that never gets an UnfreezeFrame (a known
// Arcware issue when UE loads existing project images). Timers fire from freeze start:
const FREEZE_NUDGE_MS = 2000;           // 1) gentle: request a fresh keyframe + ensure playback
const FREEZE_FORCE_MS = 6000;           // 2) force: clear the freeze locally (what UnfreezeFrame does)
const FREEZE_RECONNECT_CHECK_MS = 3000; // after (2), if still no new frames this long → reconnect

// Disconnect snapshot: Clarity records the DOM but not <video> content, so session
// recordings are black for the whole streaming segment. We keep a downscaled JPEG
// of the newest decoded frame (WebRTC video isn't CORS-tainted, so canvas capture
// is allowed) and publish it when the session drops; the emulator overlays it as a
// plain <img> — which Clarity DOES record — so recordings show what the user was
// doing at the moment the connection died. Kept small on purpose: Clarity drops
// oversized data URIs.
const SNAPSHOT_INTERVAL_MS = 2000;
const SNAPSHOT_MAX_WIDTH = 960;
const SNAPSHOT_JPEG_QUALITY = 0.7;

// Continuous background snapshots: while streaming, publish a fresh frame every
// LIVE_SNAPSHOT_MS. The emulator layers it BEHIND the live video, so the user only
// ever sees the opaque stream — but Clarity's replay (where the <video> is blank)
// shows the frame behind it, giving a time-lapse of the whole session. Smaller/
// cheaper than the disconnect snapshot because it fires repeatedly, and we skip
// near-duplicate frames (see LIVE_SNAPSHOT_MIN_DELTA) so idle/static periods add
// almost nothing — both to keep the main thread quiet and to stay under Clarity's
// per-session recording payload cap.
const LIVE_SNAPSHOT_MS = 5000;
// Resolution drives perceived sharpness more than JPEG quality does; 960px keeps the
// UE UI/text legible in the replay. Both directly scale the per-frame payload, so
// these are the dials to turn back down if Clarity recordings start getting truncated.
const LIVE_SNAPSHOT_MAX_WIDTH = 960;
const LIVE_SNAPSHOT_JPEG_QUALITY = 0.7;
// Skip-if-unchanged threshold. We compare a 16×16 thumbnail of the current frame to
// the last published one (sum of per-channel abs differences, 0..~195k) and only
// publish when it moved more than this — so a static scene publishes once, not
// every tick. Lower = more sensitive (more frames); higher = coarser (fewer).
const LIVE_SNAPSHOT_MIN_DELTA = 5000;

// Statuses that mean the UE instance behind the current session is gone for good.
const TERMINAL_STATUSES = new Set(["ended", "failed"]);

// Safety valve for the data-channel gating below. Pixel streaming opens its data
// channel as part of the same negotiation that brings up media, normally BEFORE the
// first frame — so if video is decoding and we still haven't seen a dataChannelOpen
// event this long afterwards, the likeliest explanation is that this SDK build never
// forwards the event, not that the channel is closed. Rather than queue forever (which
// would be a worse failure than the one we're fixing), assume it is usable and flush.
const CHANNEL_OPEN_FALLBACK_MS = 3000;

// Above this many messages held for a not-yet-open channel we drop the OLDEST: a queue
// this deep means the channel never opened, and replaying a long backlog helps nobody.
const MAX_QUEUED_MESSAGES = 32;

// ── Silent transport death ───────────────────────────────────────────────────
// Epic's WebRtcPlayerController listens to ICE state ONLY to announce the first
// "connected"; it ignores "disconnected" and "failed" entirely. So when the peer
// connection dies underneath a still-open signalling WebSocket — laptop sleep, Wi-Fi
// drop or network switch while the user is away — no SDK event fires, status stays
// "streaming", and the data channel can still read "open" (SCTP outlives ICE for a
// while). Everything we send is then lost: the user comes back to a login form that
// looks alive, submits, and gets "Login is taking longer than expected" 30s later.
// We watch the peer connection ourselves: "failed" is final at once; "disconnected"
// can self-heal after a blip, so it only counts once it has lasted this long.
const ICE_DISCONNECTED_GRACE_MS = 15000;
// A send channel that closes while video is still up, with no SDK disconnect event
// following it, leaves the same dead-but-healthy-looking session.
const CHANNEL_CLOSED_GRACE_MS = 5000;

const HEALTHY_ICE_STATES = new Set(["connected", "completed"]);

// ── Arcware session resumption ───────────────────────────────────────────────
// The SDK persists a session id (localStorage, falling back to sessionStorage,
// under "pxss") so a quick reload RESUMES the previous UE instance instead of
// paying for a new one: ArcwareConfig.urlFlags appends `&s=<id>&r=true` and the
// signalling server then SKIPS the "Connecting to the cloud infrastructure.."
// provisioning step and goes straight to attaching signalling.
//
// That's right for a genuine mid-session recovery and wrong once the instance is
// dead (AFK timeout, terminated session, server-side reclaim): the resume waits
// forever for an instance that no longer exists, and because there's no
// provisioning step there's nothing to fall back to — the user just sits on
// "Initiating connection to stream signaling..". It also self-perpetuates: the
// stored id is re-stamped every time the server echoes a session id (and on every
// ping), so each retry renews the dead session for another keepSession window
// instead of letting it expire.
//
// So we drop the stored id whenever it can't describe a session this tab may resume:
// on mount, on this tab's first connect, and on every terminal status. What's left is
// resumption of a session that is genuinely alive and ours — including the SDK's own
// auto-reconnect, which keeps us in "connecting" and never goes terminal.
const SESSION_STORAGE_KEY = "pxss";

const forgetStoredSession = () => {
  try { ArcwarePixelStreaming.clearSessionId?.(); } catch (_) {}
  try { window.localStorage?.removeItem(SESSION_STORAGE_KEY); } catch (_) {}
  try { window.sessionStorage?.removeItem(SESSION_STORAGE_KEY); } catch (_) {}
};

/** True when the next connect would ask the server to resume a previous instance. */
const hasStoredSession = () => {
  try {
    return Boolean(
      window.localStorage?.getItem(SESSION_STORAGE_KEY) ??
        window.sessionStorage?.getItem(SESSION_STORAGE_KEY)
    );
  } catch (_) {
    return false;
  }
};

// ── Double-click / mouse-release fixes ───────────────────────────────────────
// Two separate defects, both from how the SDK maps browser mouse events onto the
// pixel streaming wire protocol. Everything here sits in the CAPTURE phase on
// videoElementParent, which runs before the SDK's own (bubble-phase) listeners
// on that same node, so we can drop or reshape an event before it sees it.
//
// (1) DOUBLE-CLICK ARRIVES AFTER THE GESTURE, NOT DURING IT
// The SDK binds `dblclick` and from it sends `MouseDouble` (id 76), which UE
// hands to Slate as a button PRESS. But the browser fires `dblclick` only after
// the SECOND MOUSEUP — i.e. after a double-click-and-drag has already finished.
// So a double-click+drag reaches UE as:
//
//     Down, Up, Down, ...drag..., Up, MouseDouble(press)
//
// Two things are wrong. The trailing press has no release, leaving UE convinced
// the button is still held. And the drag itself ran as a PLAIN drag, because
// while it was happening UE had only seen an ordinary second press.
//
// Windows sends DOWN, UP, DBLCLK, ...drag..., UP — the double-click press
// REPLACES the second press, so it is in effect for the whole drag and the real
// mouseup releases it. That is why the same UE build behaves locally.
//
// "native-order" (default) reproduces that: on the second press we suppress the
// SDK's MouseDown and hand it a synthetic `dblclick` instead, so MouseDouble is
// sent at press time; the browser's own later `dblclick` is then dropped. The
// release is the real, untrusted-free browser mouseup with true coordinates —
// no synthesised release, which is what the previous attempt got wrong.
//
// "suppress" instead drops only the trailing `dblclick`, so UE sees plain
// down/up pairs and no double-click at all. Fallback if UE mishandles
// MouseDouble in some way we can't see from this side.
//
// "off" restores stock SDK double-click behaviour. It does not affect (2)
// below, which has its own flag.
const DOUBLE_CLICK_MODE = "native-order"; // "native-order" | "suppress" | "off"

// (2) A RELEASE OUTSIDE THE VIDEO HOST IS NEVER DELIVERED
// The SDK binds `mouseup` on videoElementParent only. Release the button while
// the pointer is outside that box — easy to do mid-drag, near an edge, or over
// one of our own overlays — and UE simply never hears it, so it keeps holding
// whatever was grabbed. We watch for that on the window and re-deliver the
// release, clamped to the host's bounds so it lands where the pointer left.
// Only fires when the release genuinely happened outside, so the ordinary
// in-bounds path is untouched.
const RECOVER_OUTSIDE_RELEASE = true;

/** Build a synthetic mouse event that mirrors a real one's position. */
const mouseEventLike = (type, { clientX, clientY, button = 0, buttons = 0 }) =>
  new MouseEvent(type, {
    bubbles: true,
    cancelable: true,
    view: window,
    clientX,
    clientY,
    button,
    buttons,
  });

/**
 * Attach the mouse-input repairs to the SDK's video host. Returns a cleanup fn.
 */
const installMouseInputFixes = (parent) => {
  // Tracks whether the current left-button press started inside the video host,
  // so we only ever re-deliver a release that the SDK actually owed UE.
  let pressActive = false;

  const onMouseDownCapture = (e) => {
    if (e.button !== 0) return;
    pressActive = true;

    // `detail` counts consecutive clicks, so every EVEN press closes a pair and
    // is a double-click press: 2, then 4, 6… if the user keeps clicking in place
    // without pausing long enough for the browser to reset the count. Windows
    // alternates DOWN, DBLCLK, DOWN, DBLCLK the same way. Matching only
    // `detail === 2` would send MouseDouble for the first double-click and then
    // silently stop for rapid repeats — the "works once, then not again unless I
    // click off and wait" behaviour.
    if (DOUBLE_CLICK_MODE !== "native-order") return;
    if (e.detail < 2 || e.detail % 2 !== 0) return;
    e.stopImmediatePropagation();
    e.preventDefault(); // the SDK's onMouseDown would have done this
    try {
      e.target.dispatchEvent(mouseEventLike("dblclick", e));
    } catch (_) {}
  };

  const onMouseUpCapture = (e) => {
    if (e.button === 0) pressActive = false;
  };

  const onDblClickCapture = (e) => {
    if (DOUBLE_CLICK_MODE === "off") return;
    // Drop the browser's own late dblclick. Ours is synthetic (isTrusted false)
    // and must pass through to the SDK.
    if (e.isTrusted) e.stopImmediatePropagation();
  };

  const onWindowMouseUp = (e) => {
    if (!RECOVER_OUTSIDE_RELEASE) return;
    if (e.button !== 0 || !pressActive) return;
    pressActive = false;
    if (parent.contains(e.target)) return; // SDK already heard it

    const rect = parent.getBoundingClientRect();
    const clamp = (v, lo, hi) => Math.min(Math.max(v, lo), hi);
    const target = parent.querySelector("video") || parent;
    try {
      target.dispatchEvent(
        mouseEventLike("mouseup", {
          // Inset by a pixel so the point stays inside the box after rounding.
          clientX: clamp(e.clientX, rect.left + 1, rect.right - 1),
          clientY: clamp(e.clientY, rect.top + 1, rect.bottom - 1),
        })
      );
    } catch (_) {}
  };

  parent.addEventListener("mousedown", onMouseDownCapture, true);
  parent.addEventListener("mouseup", onMouseUpCapture, true);
  parent.addEventListener("dblclick", onDblClickCapture, true);
  window.addEventListener("mouseup", onWindowMouseUp, true);

  return () => {
    parent.removeEventListener("mousedown", onMouseDownCapture, true);
    parent.removeEventListener("mouseup", onMouseUpCapture, true);
    parent.removeEventListener("dblclick", onDblClickCapture, true);
    window.removeEventListener("mouseup", onWindowMouseUp, true);
  };
};

export default function useArcwareStream({
  shareId,
  projectId,
  onMessage,
  onDataChannelOpen,
  onStage,
  enableLiveSnapshots = false,
} = {}) {
  const containerRef = useRef(null);
  const streamRef = useRef(null);

  const onMessageRef = useRef(onMessage);
  const onDataChannelOpenRef = useRef(onDataChannelOpen);
  const onStageRef = useRef(onStage);
  useEffect(() => { onMessageRef.current = onMessage; }, [onMessage]);
  useEffect(() => { onDataChannelOpenRef.current = onDataChannelOpen; }, [onDataChannelOpen]);
  useEffect(() => { onStageRef.current = onStage; }, [onStage]);

  const [status, setStatus] = useState("idle");
  const [ready, setReady] = useState(false);
  // Why the SDK never became usable, when that happens. Both failures below used to
  // return silently, leaving `ready` false forever with nothing said — and since no
  // connection was ever attempted, none of the connection timeouts applied either, so
  // the user simply waited. Surfaced so the caller can escalate and name the cause.
  const [initError, setInitError] = useState(null);
  const statusRef = useRef("idle");
  const statusHistoryRef = useRef([]);
  const disconnectEventsRef = useRef([]);
  const hasConnectedRef = useRef(false);
  const viewerSentRef = useRef(false);

  // ── Send readiness = the DATA CHANNEL, not the video ─────────────────────────
  // Epic's DataChannelSender silently DISCARDS anything sent while the channel is
  // not open — it logs and returns, it never throws (see DataChannelSender.sendData
  // in the SDK). So emitUIInteraction can't tell us it failed, and every message we
  // sent too early ("ViewerMode", "ImageLinkString", …) vanished with no trace.
  //
  // We used to infer channel-readiness from `videoInitialized`, which is a different
  // fact: the video path can be up while SCTP is still negotiating. That inference
  // is what made the flow latency-sensitive — the higher the RTT, the wider the gap
  // between the first frame and a channel we may actually write to.
  //
  // Now we track the real event, and anything sent before it is QUEUED and flushed
  // in order once the channel opens. Queued messages are sent exactly ONCE — this is
  // a delivery guarantee, not a retry, because several of these messages (login,
  // account creation) are not idempotent on the UE/PlayFab side.
  const [channelOpen, setChannelOpen] = useState(false);
  const channelOpenRef = useRef(false);
  const outboundQueueRef = useRef([]);

  // RTT is reported once per session; stats arrive every second and we only want the
  // order of magnitude (is this user near the streaming region, or 300ms away?).
  const rttReportedRef = useRef(false);

  // AFK inactivity handling. The Arcware dashboard's "Enable timeouts" settings
  // (warn after N s idle, then an M s countdown) arrive via streamInfo and are
  // enforced CLIENT-SIDE by Epic's AFKController. With CoreSetup we render none
  // of the SDK's UI, so without these listeners the countdown is invisible AND
  // undismissable (input only resets the timer while it's idle-watching — once
  // the warning is active, only the overlay's dismiss callback cancels it). We
  // surface the warning state so the emulator can show its own countdown, and
  // expose the SDK's dismiss callback so any user activity can cancel it.
  const [afkWarning, setAfkWarning] = useState(null); // null | { countDown }
  const [afkTimedOut, setAfkTimedOut] = useState(false);
  const dismissAfkRef = useRef(null);

  // Last video frame at the moment the session dropped (JPEG data URI), for the
  // emulator's freeze-frame overlay. Null while connected / before first drop.
  const [disconnectSnapshot, setDisconnectSnapshot] = useState(null);

  // Latest frame published for the behind-the-video Clarity time-lapse (JPEG data
  // URI). Only updates when enableLiveSnapshots is on. Kept across reconnect gaps.
  const [liveSnapshot, setLiveSnapshot] = useState(null);

  // Arcware infra status messages ("love letters" from the signaling server:
  // "Connecting to the cloud infrastructure..", "Session ticket registered
  // successfully.", …). With CoreSetup we render none of the SDK's UI, so its
  // default status overlay never shows — we surface these in our own loading
  // UI instead. Kept as plain text so Clarity picks them up. Capped to the
  // last 8; cleared on every play() so a new attempt starts clean.
  const [connectionMessages, setConnectionMessages] = useState([]);
  const messageIdRef = useRef(0);

  // Step-by-step record of SDK bring-up, from credentials arriving to the video host
  // being mounted. This is the stretch where a failure leaves `ready` false and no
  // connection is ever attempted, so there are no connection events to inspect —
  // without this trace there is nothing at all to look at.
  const bootTraceRef = useRef([]);
  const trace = useCallback((step, detail) => {
    const at = Math.round(performance.now());
    bootTraceRef.current = [...bootTraceRef.current.slice(-29), { at, step, detail: detail ?? null }];
    console.log(`[arcware:boot +${at}ms] ${step}`, detail ?? "");
  }, []);

  const pushDiag = useCallback((type, detail) => {
    disconnectEventsRef.current = [
      ...disconnectEventsRef.current.slice(-49),
      { time: new Date().toISOString(), type, detail: detail ?? null },
    ];
  }, []);

  const applyStatus = useCallback((next) => {
    if (!next || next === statusRef.current) return;
    const prev = statusRef.current;
    statusRef.current = next;
    statusHistoryRef.current = [
      ...statusHistoryRef.current.slice(-9),
      `${new Date().toISOString()} ${prev ?? "null"} → ${next}`,
    ];
    // The session behind a terminal status is unrecoverable, so the stored session
    // id can only mislead the next connect into resuming a dead instance — and any
    // message still queued for it belongs to a UE instance that no longer exists.
    if (TERMINAL_STATUSES.has(next)) {
      forgetStoredSession();
      outboundQueueRef.current = [];
    }
    setStatus(next);
  }, []);

  useEffect(() => {
    let disposed = false;

    // Credentials arrive asynchronously (fetched from the admin-managed settings doc).
    // Don't spin up CoreSetup with an empty Share ID — bail until it's populated. This
    // effect re-runs when shareId changes (see deps below) and initializes then.
    if (!shareId) {
      trace("waiting-for-credentials");
      return;
    }
    trace("credentials-received", { shareId, projectId: projectId || "(none)" });

    // Start fresh on every page load: a resumed session means UE never re-emits
    // RenderNow_Loaded, so the login/loading flow never re-runs. Reconnects WITHIN a
    // page load can still resume while the session is alive — see forgetStoredSession.
    forgetStoredSession();

    let stream;
    try {
      const config = new ArcwareConfig({
        initialSettings: {
          AutoConnect: false,
          AutoPlayVideo: true,
          StartVideoMuted: true,
          // We don't use the microphone; UseMic defaults to true, which triggers the
          // browser's mic-permission prompt. Disable it so no prompt ever appears.
          UseMic: false,
        },
        settings: {
          shareId: shareId || "",
          ...(projectId ? { projectId } : {}),
          // Hide all built-in Arcware UI — our own overlay handles everything.
          fullscreenButton: false,
          audioButton: false,
          settingsButton: false,
          infoButton: false,
          micButton: false,
          stopButton: false,
          connectionStrengthIcon: false,
        },
      });
      // forceRefresh: CoreSetup keeps a MODULE-LEVEL singleton keyed by
      // shareId|projectId. Without this, a remount would hand us back the very
      // instance our previous cleanup already disconnect()ed. Force a fresh one
      // so every mount owns a live PixelStreaming instance.
      ({ PixelStreaming: stream } = CoreSetup(config, undefined, { forceRefresh: true }));
    } catch (e) {
      console.error("[arcware] CoreSetup failed:", e);
      trace("sdk-init-failed", String(e?.message ?? e));
      setInitError(`SDK init failed: ${e?.message ?? e}`);
      return undefined;
    }
    trace("sdk-created");
    streamRef.current = stream;

    // ── Freeze-frame auto-resume (escalating) ─────────────────────
    // If a UE freeze-frame lasts too long without UE sending UnfreezeFrame, recover in
    // escalating steps so we rarely disturb a legitimate (self-resolving) freeze:
    //   1) nudge  — request a fresh keyframe + ensure playback (non-destructive)
    //   2) force  — locally do what UnfreezeFrame does (clear freeze image + re-enable track)
    //   3) reconnect — only if the video still isn't decoding new frames after the force
    let freezeTimers = [];
    let freezeStart = 0;
    const clearFreezeTimers = () => { freezeTimers.forEach((t) => clearTimeout(t)); freezeTimers = []; };

    // Count of decoded video frames — used to tell whether the video actually resumed.
    const getFrameCount = () => {
      try {
        const v = stream?.videoElementParent?.querySelector?.("video");
        if (!v) return 0;
        return v.getVideoPlaybackQuality?.().totalVideoFrames ?? Math.floor((v.currentTime || 0) * 1000);
      } catch (_) { return 0; }
    };

    // ── Disconnect snapshot (freeze-frame for Clarity) ───────────
    // Every SNAPSHOT_INTERVAL_MS while streaming, stash a small JPEG of the newest
    // decoded frame. On disconnect we try one final capture (the <video> element
    // usually retains its last frame after WebRTC dies) and fall back to the
    // periodic one, then publish it via disconnectSnapshot state.
    let lastFrameUri = null;
    const snapshotCanvas = document.createElement("canvas");

    const captureFrame = (maxWidth = SNAPSHOT_MAX_WIDTH, quality = SNAPSHOT_JPEG_QUALITY) => {
      try {
        const v = stream?.videoElementParent?.querySelector?.("video");
        if (!v || v.readyState < 2 || !v.videoWidth) return null;
        const scale = Math.min(1, maxWidth / v.videoWidth);
        snapshotCanvas.width = Math.round(v.videoWidth * scale);
        snapshotCanvas.height = Math.round(v.videoHeight * scale);
        // willReadFrequently: every capture calls getImageData below (the black-frame
        // check), which the browser warns about on a GPU-backed canvas.
        const ctx = snapshotCanvas.getContext("2d", { willReadFrequently: true });
        ctx.drawImage(v, 0, 0, snapshotCanvas.width, snapshotCanvas.height);
        // An ended/cleared track paints solid black — return null so the caller
        // keeps the previous good frame. Real UE scenes always have lit pixels.
        const px = ctx.getImageData(0, 0, snapshotCanvas.width, snapshotCanvas.height).data;
        let lit = 0;
        for (let i = 0; i < px.length; i += 40) { // sample every 10th pixel
          if (px[i] > 16 || px[i + 1] > 16 || px[i + 2] > 16) { lit++; if (lit > 20) break; }
        }
        if (lit <= 20) return null;
        return snapshotCanvas.toDataURL("image/jpeg", quality);
      } catch (_) { return null; }
    };

    const snapshotTimer = setInterval(() => {
      if (disposed || statusRef.current !== "streaming") return;
      const uri = captureFrame();
      if (uri) lastFrameUri = uri;
    }, SNAPSHOT_INTERVAL_MS);

    // Behind-the-video time-lapse: publish a fresh (smaller) frame on its own cadence
    // for the emulator to render behind the stream. A cheap 16×16 fingerprint lets us
    // skip frames that barely changed, so a static scene (idle user, reading the
    // tutorial) publishes once instead of every tick.
    const fpCanvas = document.createElement("canvas");
    fpCanvas.width = 16;
    fpCanvas.height = 16;
    const fpCtx = fpCanvas.getContext("2d", { willReadFrequently: true });
    const frameFingerprint = () => {
      try {
        const v = stream?.videoElementParent?.querySelector?.("video");
        if (!v || v.readyState < 2 || !v.videoWidth) return null;
        fpCtx.drawImage(v, 0, 0, 16, 16);
        return fpCtx.getImageData(0, 0, 16, 16).data; // 256 px × 4 = 1 KB
      } catch (_) { return null; }
    };
    const fpDelta = (a, b) => {
      if (!a || !b) return Infinity; // no baseline yet → treat as changed (publish)
      let d = 0;
      for (let i = 0; i < a.length; i += 4) {
        d += Math.abs(a[i] - b[i]) + Math.abs(a[i + 1] - b[i + 1]) + Math.abs(a[i + 2] - b[i + 2]);
      }
      return d;
    };

    let lastFp = null;
    const liveSnapshotTimer = enableLiveSnapshots
      ? setInterval(() => {
          if (disposed || statusRef.current !== "streaming") return;
          const fp = frameFingerprint();
          if (lastFp && fpDelta(fp, lastFp) < LIVE_SNAPSHOT_MIN_DELTA) return; // ~unchanged → skip
          const uri = captureFrame(LIVE_SNAPSHOT_MAX_WIDTH, LIVE_SNAPSHOT_JPEG_QUALITY);
          if (uri) { lastFp = fp; setLiveSnapshot(uri); }
        }, LIVE_SNAPSHOT_MS)
      : null;

    // Publish the freeze-frame. Only fires while status is still "streaming" so
    // intentional teardowns (play()'s internal reconnect, user cancel while
    // pending) don't resurface a stale frame.
    const publishDisconnectSnapshot = () => {
      if (disposed || statusRef.current !== "streaming") return;
      const uri = captureFrame() ?? lastFrameUri;
      if (!uri) return;
      lastFrameUri = uri;
      pushDiag("disconnectSnapshot");
      setDisconnectSnapshot(uri);
    };

    // Step 1 — gentle nudge. Doesn't touch the freeze state, so it can't disrupt a legit freeze.
    const nudgeResume = () => {
      if (disposed) return;
      pushDiag("autoResumeNudge");
      const wrtc = stream?._webRtcController;
      try { wrtc?.streamMessageController?.toStreamerHandlers?.get?.("IFrameRequest")?.(); } catch (_) {}
      try { stream?.play?.(); } catch (_) {}
      try { const v = stream?.videoElementParent?.querySelector?.("video"); v?.play?.().catch(() => {}); } catch (_) {}
    };

    // Step 2 — force the unfreeze locally, then verify the video is actually decoding again;
    // if not, escalate to a reconnect (step 3).
    const forceResume = () => {
      if (disposed) return;
      pushDiag("autoResumeForce");
      const wrtc = stream?._webRtcController;
      try { wrtc?.invalidateFreezeFrameAndEnableVideo?.(); } catch (_) {}
      nudgeResume();
      const before = getFrameCount();
      freezeTimers.push(setTimeout(() => {
        if (disposed) return;
        // A hidden tab stops decoding video, so the frame count can't advance and this
        // check would "prove" a freeze that isn't real — then tear down a perfectly
        // healthy connection. That matters most during Google sign-in, where the user
        // is deliberately away in another tab and UE still has to reach us with its
        // second open_url. Leave the freeze timers cleared; a genuine freeze will be
        // re-armed by the next FreezeFrame once the tab is visible again.
        if (document.hidden) {
          pushDiag("autoResumeReconnectSkippedHidden");
          console.log("[arcware] tab hidden — skipping freeze reconnect (frames can't advance)");
          return;
        }
        if (getFrameCount() <= before) {
          pushDiag("autoResumeReconnect");
          try { stream?.reconnect?.(); } catch (_) {}
        }
      }, FREEZE_RECONNECT_CHECK_MS));
    };

    // ── UE → app messages ──────────────────────────────────────
    // Our UE build speaks a plain-string protocol ("SDK_Login_Success",
    // "RenderNow_Loaded", "Veo_Link:<url>", image URLs, …). Epic decodes every UE
    // "Response" data-channel message to a STRING and dispatches it to response
    // listeners keyed by name. Arcware registers "ue-response" → handleUEResponse in
    // its constructor, but that pushes the payload through a JSON router (analytics /
    // filetransfer) that drops our non-JSON strings, so applicationResponseHandler
    // never fires for them. We take over that channel and deliver the raw string
    // straight to handleMessage — exactly what Interlucent's `ue-command-response` gave.
    const onResp = (raw) => { if (!disposed) onMessageRef.current?.(raw); };

    // ── Unknown FromStreamer message types ──────────────────────
    // Epic dispatches with:
    //     const type = fromStreamerMessages.get(message[0]);
    //     fromStreamerHandlers.get(type)(event.data);
    // and wrapped it in a try/catch that warned "message type unknown to the Pixel
    // Streaming protocol". Arcware shipped that catch COMMENTED OUT, so an id the SDK
    // doesn't know throws a TypeError out of dataChannel.onmessage and the message is
    // destroyed — which is exactly how UE's reply to our Google trigger disappeared.
    //
    // The ids come from a fixed table (0-14, 255) that UE's own Protocol message can
    // only override for types the SDK already handles; anything else is skipped and
    // then crashes on arrival. A UE build on a different Pixel Streaming version than
    // this SDK's ue5.7 frontend therefore breaks here.
    //
    // Restore the missing resilience, name the offending id, and recover the payload
    // when it is one of ours. Idempotent: _webRtcController is rebuilt on reconnect,
    // so this runs again and the flag stops it double-wrapping.
    const guardUnknownMessageTypes = () => {
      const handlers = stream?._webRtcController?.streamMessageController
        ?.fromStreamerHandlers;
      if (!handlers?.get || handlers.__rnUnknownGuard) return false;
      const originalGet = handlers.get.bind(handlers);
      handlers.get = (key) => {
        const handler = originalGet(key);
        if (typeof handler === "function") return handler;
        return (data) => {
          let typeByte;
          let decoded = null;
          try {
            typeByte = new Uint8Array(data)[0];
            decoded = new TextDecoder("utf-16").decode(data.slice(1));
          } catch (_) { /* binary payload — nothing readable to show */ }
          console.warn(
            `[arcware] unknown FromStreamer message type ${typeByte} — the SDK has no ` +
              "handler for this id (UE/PixelStreaming protocol mismatch). Payload:",
            decoded
          );
          pushDiag("unknownMessageType", { typeByte, preview: decoded?.slice(0, 120) });
          // Forward anything that decoded to clean text. Our UE protocol is mostly
          // plain strings — RenderNow_Loaded, SDK_Login_Success, Veo_Link:<url>, image
          // URLs, raw PlayFab error text — so a JSON-only gate silently swallowed them,
          // SDK_Login_Success included, which would break login on its own. Binary
          // frames (freeze frames, file transfers) decode to replacement characters and
          // NULs, so those are rejected here and go no further. handleMessage ignores
          // anything it doesn't recognise, so a stray string is harmless.
          const isCleanText =
            !!decoded && !decoded.includes("\uFFFD") && !decoded.includes("\u0000");
          if (!isCleanText) return;
          console.warn("[arcware] recovering it as a Response — see the mismatch above");
          if (!disposed) onMessageRef.current?.(decoded);
        };
      };
      handlers.__rnUnknownGuard = true;
      trace("unknown-message-guard-installed");
      return true;
    };

    // ── Derived status + diagnostic signals ─────────────────────
    const onQueue = (q) => { if (disposed) return; pushDiag("queue", q?.queue ?? q); applyStatus("queued"); };
    const onError = (err) => { if (disposed) return; pushDiag("error", err); applyStatus("failed"); };
    const onWsClose = (ev) => {
      if (disposed) return;
      publishDisconnectSnapshot();
      pushDiag("websocketClose", { code: ev?.code, reason: ev?.reason });
      // The next status is decided by webRtcDisconnected (fired for every Epic-
      // transport close), because it knows whether the SDK will auto-reconnect —
      // flipping to "ended" here would kill sessions the SDK is about to recover.
      // Fallback: if that event somehow doesn't follow this close, don't leave a
      // dead session stuck in "streaming" forever.
      setTimeout(() => {
        if (disposed) return;
        if (statusRef.current === "streaming") applyStatus("ended");
      }, 500);
    };
    // Fires on every signaling close and carries WHY the connection ended plus
    // whether the SDK will retry by itself (up to MaxReconnectAttempts, 2 s apart).
    // While it retries we stay in "connecting" — the emulator shows "Reconnecting…"
    // and only escalates to the Connection Lost modal if recovery takes too long.
    const onWebRtcDisconnected = (ev) => {
      if (disposed) return;
      publishDisconnectSnapshot();
      const reason = ev?.data?.eventString ?? "";
      const willAutoReconnect = ev?.data?.allowClickToReconnect === false;
      pushDiag("webRtcDisconnected", { reason, willAutoReconnect });
      applyStatus(willAutoReconnect ? "connecting" : "ended");
    };

    // ── AFK warning countdown + timeout ─────────────────────────
    // Note: the activate event fires with the PREVIOUS countdown value (the SDK
    // resets it just after dispatching); the per-second update events carry the
    // real remaining time, so the overlay count comes from those.
    const onAfkWarningActivate = (ev) => {
      if (disposed) return;
      pushDiag("afkWarningActivate");
      dismissAfkRef.current = ev?.data?.dismissAfk ?? null;
      setAfkWarning({ countDown: null });
    };
    const onAfkWarningUpdate = (ev) => {
      if (disposed) return;
      setAfkWarning({ countDown: ev?.data?.countDown ?? null });
    };
    const onAfkWarningDeactivate = () => {
      if (disposed) return;
      pushDiag("afkWarningDeactivate");
      dismissAfkRef.current = null;
      setAfkWarning(null);
    };
    const onAfkTimedOut = () => {
      if (disposed) return;
      publishDisconnectSnapshot();
      pushDiag("afkTimedOut");
      dismissAfkRef.current = null;
      setAfkWarning(null);
      setAfkTimedOut(true);
    };
    // ── Transport watchdog (see ICE_DISCONNECTED_GRACE_MS) ──────
    // Only acts while we believe we're streaming: in any other state the SDK's own
    // events and the emulator's reconnect/terminal timers already own the outcome.
    let watchedPc = null;
    let iceGraceTimer = null;
    let channelClosedTimer = null;

    const clearTransportTimers = () => {
      if (iceGraceTimer) { clearTimeout(iceGraceTimer); iceGraceTimer = null; }
      if (channelClosedTimer) { clearTimeout(channelClosedTimer); channelClosedTimer = null; }
    };

    const declareTransportDead = (cause, detail) => {
      clearTransportTimers();
      if (disposed || statusRef.current !== "streaming") return;
      console.warn(`[arcware] transport dead while status was "streaming" (${cause})`, detail ?? "");
      publishDisconnectSnapshot();
      pushDiag("transportDead", { cause, ...(detail || {}) });
      onStageRef.current?.("transport_dead", { cause });
      channelOpenRef.current = false;
      setChannelOpen(false);
      // "ended" routes through the emulator's terminal path: the Connection Lost modal,
      // which sits above the login overlay, instead of a login form that can't work.
      applyStatus("ended");
    };

    const onPcStateChange = () => {
      const pc = watchedPc;
      if (disposed || !pc) return;
      const ice = pc.iceConnectionState;
      const conn = pc.connectionState;
      if (ice === "failed" || conn === "failed") {
        declareTransportDead("ice_failed", { ice, conn });
        return;
      }
      if (ice === "disconnected" || conn === "disconnected") {
        if (!iceGraceTimer) {
          pushDiag("iceDisconnected", { ice, conn });
          iceGraceTimer = setTimeout(() => {
            iceGraceTimer = null;
            if (pc !== watchedPc) return;
            if (HEALTHY_ICE_STATES.has(pc.iceConnectionState)) return;
            declareTransportDead("ice_disconnected", {
              ice: pc.iceConnectionState,
              conn: pc.connectionState,
            });
          }, ICE_DISCONNECTED_GRACE_MS);
        }
        return;
      }
      if (HEALTHY_ICE_STATES.has(ice) && iceGraceTimer) {
        clearTimeout(iceGraceTimer);
        iceGraceTimer = null;
        pushDiag("iceRecovered", { ice });
      }
    };

    // The SDK builds a new RTCPeerConnection on every (re)connect, so this re-runs from
    // the video/channel-open hooks and moves the listeners onto the current one.
    // addEventListener, not on*: the SDK owns oniceconnectionstatechange.
    const watchPeerConnection = () => {
      const pc = stream?._webRtcController?.peerConnectionController?.peerConnection;
      if (!pc || pc === watchedPc) return;
      if (watchedPc) {
        watchedPc.removeEventListener("iceconnectionstatechange", onPcStateChange);
        watchedPc.removeEventListener("connectionstatechange", onPcStateChange);
      }
      clearTransportTimers();
      watchedPc = pc;
      pc.addEventListener("iceconnectionstatechange", onPcStateChange);
      pc.addEventListener("connectionstatechange", onPcStateChange);
      onPcStateChange(); // the state may already have moved before we attached
    };

    // ── Data channel: the real "we can talk to UE now" signal ───────────────────
    // In SFU mode the SDK opens separate send/recv channels; only the send side
    // tells us anything about our ability to write. Peer-to-peer uses one
    // bidirectional "datachannel" for both.
    let channelFallbackTimer = null;

    /** Open the send path, flush anything held for it, and release the caller's hook. */
    const markChannelOpen = (label) => {
      if (channelOpenRef.current) return;
      if (channelFallbackTimer) { clearTimeout(channelFallbackTimer); channelFallbackTimer = null; }

      channelOpenRef.current = true;
      setChannelOpen(true);
      if (channelClosedTimer) { clearTimeout(channelClosedTimer); channelClosedTimer = null; }
      // By now _webRtcController exists, and a reconnect will have replaced the one
      // guarded at setup — so (re-)install it here.
      try { guardUnknownMessageTypes(); } catch (e) {
        console.error("[arcware] unknown-message guard failed:", e);
      }
      try { watchPeerConnection(); } catch (e) {
        console.error("[arcware] peer-connection watch failed:", e);
      }
      trace("data-channel-open", { label });
      onStageRef.current?.("data_channel_open", { label, videoUp: statusRef.current === "streaming" });

      // Flush anything written before the channel existed, in the order it was
      // written. Each message goes out exactly once — see the queue notes above.
      const pending = outboundQueueRef.current;
      outboundQueueRef.current = [];
      if (pending.length) {
        trace("outbound-queue-flush", { count: pending.length });
        pushDiag("outboundQueueFlush", { count: pending.length });
        pending.forEach((msg) => {
          try { stream.emitUIInteraction(msg); } catch (e) {
            console.error("[arcware] queued emitUIInteraction failed:", e);
          }
        });
      }

      // Hand off to the emulator (sends ViewerMode for share links). This used to
      // hang off videoInitialized, which could fire while the channel was still
      // closed — so the token was silently discarded.
      if (!viewerSentRef.current) {
        viewerSentRef.current = true;
        onDataChannelOpenRef.current?.();
      }
    };

    const onVideoInit = () => {
      if (disposed) return;
      pushDiag("videoInitialized");
      onStageRef.current?.("video_initialized", { channelOpen: channelOpenRef.current });
      setDisconnectSnapshot(null); // stream is live again — drop the freeze-frame
      applyStatus("streaming");
      try { watchPeerConnection(); } catch (e) {
        console.error("[arcware] peer-connection watch failed:", e);
      }
      // Media is flowing, so the peer connection is up. If the channel event doesn't
      // follow shortly, stop waiting for it — see CHANNEL_OPEN_FALLBACK_MS.
      if (!channelOpenRef.current && !channelFallbackTimer) {
        channelFallbackTimer = setTimeout(() => {
          channelFallbackTimer = null;
          if (disposed || channelOpenRef.current) return;
          console.warn(
            "[arcware] video is decoding but no dataChannelOpen event arrived — " +
              "assuming the send path is usable"
          );
          pushDiag("dataChannelAssumedOpen");
          markChannelOpen("assumed");
        }, CHANNEL_OPEN_FALLBACK_MS);
      }
    };

    const onChannelOpen = (ev) => {
      if (disposed) return;
      const label = ev?.data?.label ?? "datachannel";
      pushDiag("dataChannelOpen", { label });
      if (label === "recv-datachannel") return; // receive-only: not a send path
      markChannelOpen(label);
    };

    const onChannelClose = (ev) => {
      if (disposed) return;
      const label = ev?.data?.label ?? "datachannel";
      pushDiag("dataChannelClose", { label });
      if (label === "recv-datachannel") return;
      channelOpenRef.current = false;
      setChannelOpen(false);
      // Let the next open re-send ViewerMode: the SDK's auto-reconnect builds a new
      // peer connection, and UE on the other side has no memory of the old one.
      viewerSentRef.current = false;
      // A genuine drop moves status off "streaming" right behind this event (a
      // webRtcDisconnected or websocket close). If nothing does, the session only
      // LOOKS alive — see CHANNEL_CLOSED_GRACE_MS.
      if (statusRef.current === "streaming" && !channelClosedTimer) {
        channelClosedTimer = setTimeout(() => {
          channelClosedTimer = null;
          if (channelOpenRef.current) return;
          declareTransportDead("data_channel_closed", { label });
        }, CHANNEL_CLOSED_GRACE_MS);
      }
    };

    const onChannelError = (ev) => {
      if (disposed) return;
      pushDiag("dataChannelError", { label: ev?.data?.label ?? null });
    };

    // Round-trip time, for correlating failures with distance from the streaming
    // region. Reported once per session (the first reading with a real value) —
    // stats fire every second and we only need the order of magnitude.
    const onStats = (ev) => {
      if (disposed || rttReportedRef.current) return;
      const rtt = ev?.data?.aggregatedStats?.candidatePair?.currentRoundTripTime;
      if (typeof rtt !== "number" || !Number.isFinite(rtt)) return;
      rttReportedRef.current = true;
      const rttMs = Math.round(rtt * 1000);
      pushDiag("rtt", { rttMs });
      onStageRef.current?.("rtt", { rttMs });
    };
    const onSessionId = (id) => {
      if (disposed) return;
      pushDiag("sessionId", { sessionId: id });
      onStageRef.current?.("session_id");
    };
    const onLoveLetter = (msg) => {
      if (disposed) return;
      pushDiag("loveLetter", msg);
      // The SDK's own overlay shows letter.reason verbatim (minus an "LL: "
      // prefix); mirror that so users see the same text Arcware's UI would.
      const text = typeof msg?.reason === "string" ? msg.reason.replace("LL: ", "").trim() : "";
      if (!text) return;
      setConnectionMessages((prev) => {
        if (prev[prev.length - 1]?.text === text) return prev; // collapse repeats
        return [...prev.slice(-7), { id: ++messageIdRef.current, text }];
      });
    };
    const onStreamingState = (isStreaming) => {
      if (disposed) return;
      pushDiag("streamingState", { isStreaming });
      // Deliberately NOT a promotion to "streaming". The SDK raises this as soon as the
      // signalling session is active — measured at 0.6s after connect, with the actual
      // video arriving 45.2s later, the whole gap being cloud provisioning. Treating it
      // as "streaming" meant we claimed a live stream for those 44s: the pending
      // overlay never appeared, the loading UI sat in its final phase, and
      // hasEverStreamed was set before anything had streamed (so a failure during
      // provisioning was reported as a lost connection rather than a failed start).
      // videoInitializedHandler is the honest signal and already promotes; only the
      // DROP is handled here.
      if (!isStreaming && statusRef.current === "streaming") {
        publishDisconnectSnapshot();
        applyStatus("ended");
      }
    };

    // ── Freeze-frame events (drive the escalation above) ────────
    const onLoadFreeze = () => {
      if (disposed) return;
      pushDiag("loadFreezeFrame");
      clearFreezeTimers();
      freezeStart = Date.now();
      freezeTimers.push(setTimeout(nudgeResume, FREEZE_NUDGE_MS));
      freezeTimers.push(setTimeout(forceResume, FREEZE_FORCE_MS));
    };
    const onHideFreeze = () => {
      if (disposed) return;
      // TEMP (tuning): measure how long freezes that UE resolves ON ITS OWN take, so the
      // thresholds above can be set just past that. Stuck freezes never reach here — we
      // force them instead — so every number logged here is a *legitimate* freeze duration.
      const freezeMs = freezeStart ? Date.now() - freezeStart : null;
      if (freezeMs != null) console.log(`[arcware] freeze resolved by UE in ${freezeMs}ms`);
      pushDiag("hideFreezeFrame", { freezeMs });
      freezeStart = 0;
      clearFreezeTimers();
    };

    // Own the raw UE response channel (replaces Arcware's JSON-routing "ue-response"
    // listener). Epic's response controller is created in the PixelStreaming
    // constructor and reused across connect/reconnect, so this registers once and
    // persists for the instance's lifetime.
    try {
      if (typeof stream.addResponseEventListener === "function") {
        try { stream.removeResponseEventListener?.("ue-response"); } catch (_) {}
        stream.addResponseEventListener("ue-response", onResp);
      } else {
        stream.applicationResponseHandler?.add(onResp);
      }
    } catch (e) {
      console.error("[arcware] response handler registration failed:", e);
    }

    // _webRtcController may not exist until connect; the data-channel-open hook below
    // retries, so a miss here is fine.
    try { guardUnknownMessageTypes(); } catch (e) {
      console.error("[arcware] unknown-message guard failed:", e);
    }

    try { stream.queueHandler?.add(onQueue); } catch (_) {}
    try { stream.errorHandler?.add(onError); } catch (_) {}
    try { stream.websocketOnCloseHandler?.add(onWsClose); } catch (_) {}
    try { stream.videoInitializedHandler?.add(onVideoInit); } catch (_) {}
    try { stream.sessionIdHandler?.add(onSessionId); } catch (_) {}
    try { stream.loveLetterHandler?.add(onLoveLetter); } catch (_) {}
    try { stream.onStreamingStateChange?.(onStreamingState); } catch (_) {}
    try { stream.addEventListener?.("loadFreezeFrame", onLoadFreeze); } catch (_) {}
    try { stream.addEventListener?.("hideFreezeFrame", onHideFreeze); } catch (_) {}
    try { stream.addEventListener?.("dataChannelOpen", onChannelOpen); } catch (_) {}
    try { stream.addEventListener?.("dataChannelClose", onChannelClose); } catch (_) {}
    try { stream.addEventListener?.("dataChannelError", onChannelError); } catch (_) {}
    try { stream.addEventListener?.("statsReceived", onStats); } catch (_) {}
    try { stream.addEventListener?.("webRtcDisconnected", onWebRtcDisconnected); } catch (_) {}
    try { stream.addEventListener?.("afkWarningActivate", onAfkWarningActivate); } catch (_) {}
    try { stream.addEventListener?.("afkWarningUpdate", onAfkWarningUpdate); } catch (_) {}
    try { stream.addEventListener?.("afkWarningDeactivate", onAfkWarningDeactivate); } catch (_) {}
    try { stream.addEventListener?.("afkTimedOut", onAfkTimedOut); } catch (_) {}

    // Mount the SDK's video host into our container (the SDK creates one if we don't
    // provide it via overrides). It should exist synchronously, but poll defensively.
    let rafId = null;
    let tries = 0;
    let removeMouseInputFixes = null;
    const mount = () => {
      if (disposed) return;
      const el = stream.videoElementParent;
      if (el instanceof Node && containerRef.current) {
        if (el.parentNode !== containerRef.current) containerRef.current.appendChild(el);
        if (!removeMouseInputFixes) removeMouseInputFixes = installMouseInputFixes(el);
        trace("video-host-mounted", { tries });
        setInitError(null);
        setReady(true);
        return;
      }
      if (tries++ < 300) {
        rafId = requestAnimationFrame(mount);
        return;
      }
      // Out of retries (~5s of frames). Note rAF is throttled or stopped entirely in a
      // background tab, so this can also mean the page was never in the foreground long
      // enough — either way the stream can't start and the caller has to be told.
      const cause = !(el instanceof Node) ? "SDK video host missing" : "stream container missing";
      console.error(`[arcware] video host never mounted (${cause})`);
      trace("video-host-mount-timeout", {
        tries,
        hasVideoElementParent: el instanceof Node,
        hasContainer: Boolean(containerRef.current),
        hidden: document.hidden,
      });
      setInitError(`Stream could not initialise: ${cause}`);
    };
    mount();

    return () => {
      disposed = true;
      clearFreezeTimers();
      clearTransportTimers();
      if (watchedPc) {
        try {
          watchedPc.removeEventListener("iceconnectionstatechange", onPcStateChange);
          watchedPc.removeEventListener("connectionstatechange", onPcStateChange);
        } catch (_) {}
        watchedPc = null;
      }
      if (channelFallbackTimer) clearTimeout(channelFallbackTimer);
      clearInterval(snapshotTimer);
      if (liveSnapshotTimer) clearInterval(liveSnapshotTimer);
      if (rafId) cancelAnimationFrame(rafId);
      try { removeMouseInputFixes?.(); } catch (_) {}
      removeMouseInputFixes = null;
      try { stream.removeResponseEventListener?.("ue-response"); } catch (_) {}
      try { stream.applicationResponseHandler?.remove(onResp); } catch (_) {}
      try { stream.queueHandler?.remove(onQueue); } catch (_) {}
      try { stream.errorHandler?.remove(onError); } catch (_) {}
      try { stream.websocketOnCloseHandler?.remove(onWsClose); } catch (_) {}
      try { stream.videoInitializedHandler?.remove(onVideoInit); } catch (_) {}
      try { stream.sessionIdHandler?.remove(onSessionId); } catch (_) {}
      try { stream.loveLetterHandler?.remove(onLoveLetter); } catch (_) {}
      try { stream.removeEventListener?.("loadFreezeFrame", onLoadFreeze); } catch (_) {}
      try { stream.removeEventListener?.("hideFreezeFrame", onHideFreeze); } catch (_) {}
      try { stream.removeEventListener?.("dataChannelOpen", onChannelOpen); } catch (_) {}
      try { stream.removeEventListener?.("dataChannelClose", onChannelClose); } catch (_) {}
      try { stream.removeEventListener?.("dataChannelError", onChannelError); } catch (_) {}
      try { stream.removeEventListener?.("statsReceived", onStats); } catch (_) {}
      try { stream.removeEventListener?.("webRtcDisconnected", onWebRtcDisconnected); } catch (_) {}
      try { stream.removeEventListener?.("afkWarningActivate", onAfkWarningActivate); } catch (_) {}
      try { stream.removeEventListener?.("afkWarningUpdate", onAfkWarningUpdate); } catch (_) {}
      try { stream.removeEventListener?.("afkWarningDeactivate", onAfkWarningDeactivate); } catch (_) {}
      try { stream.removeEventListener?.("afkTimedOut", onAfkTimedOut); } catch (_) {}
      try { stream.disconnect?.(); } catch (_) {}
      try {
        const el = stream.videoElementParent;
        if (el && el.parentNode) el.parentNode.removeChild(el);
      } catch (_) {}
      streamRef.current = null;
      // This SDK instance is gone; nothing queued for it can ever be delivered.
      channelOpenRef.current = false;
      outboundQueueRef.current = [];
      rttReportedRef.current = false;
    };
  }, [shareId, projectId, applyStatus, pushDiag, trace, enableLiveSnapshots]);

  // Start / resume the stream. First call connects; later calls reconnect (Arcware's
  // reconnect() disconnects any live connection first, then re-establishes).
  const play = useCallback(() => {
    const stream = streamRef.current;
    if (!stream) return;
    setAfkTimedOut(false);
    setAfkWarning(null);
    setDisconnectSnapshot(null);
    setConnectionMessages([]);
    // The first connect of a tab must never inherit a stored session id: the storage
    // key is shared across tabs and a tab that's still streaming re-stamps it on every
    // ping, so a second /render tab would otherwise try to resume the first one's live
    // instance. Later connects from this tab are resuming OUR own session, which is
    // what resumption is for.
    const firstConnect = !hasConnectedRef.current;
    if (firstConnect) forgetStoredSession();
    // Recorded because it decides which connection flow the user sees: with a stored
    // session the server skips provisioning ("Connecting to the cloud
    // infrastructure..") and attaches straight to signalling, so a connect that looks
    // stuck at the signalling step is the first thing to check here.
    pushDiag("connectAttempt", { resume: hasStoredSession() });
    trace("connect-called", { resume: hasStoredSession(), firstConnect });
    applyStatus("connecting");
    try {
      if (firstConnect) {
        hasConnectedRef.current = true;
        stream.connect();
      } else {
        stream.reconnect();
      }
    } catch (e) {
      console.error("[arcware] play/connect failed:", e);
    }
  }, [applyStatus, pushDiag, trace]);

  const disconnect = useCallback(() => {
    const stream = streamRef.current;
    try { stream?.disconnect?.(); } catch (e) { console.error("[arcware] disconnect failed:", e); }
  }, []);

  // TEST ONLY (gated behind ?debugFreezeFrame=1 in the emulator): force a genuine
  // disconnect so the freeze-frame overlay + Connection Lost flow can be seen in a
  // real environment. Uses the SDK's own disconnect() so it travels the exact prod
  // path — webRtcDisconnected fires while status is still "streaming", which
  // publishes the snapshot, then status settles to "ended".
  const debugForceDisconnect = useCallback(() => {
    const stream = streamRef.current;
    try { console.log("[arcware] debugForceDisconnect — simulating a genuine drop"); } catch (_) {}
    try { stream?.disconnect?.(); } catch (e) { console.error("[arcware] debugForceDisconnect failed:", e); }
  }, []);

  /** Hand a message straight to the SDK. Returns false if there was no stream at all. */
  const emitNow = useCallback((msg) => {
    const stream = streamRef.current;
    if (!stream?.emitUIInteraction) return false;
    try {
      stream.emitUIInteraction(msg);
      return true;
    } catch (e) {
      console.error("[arcware] emitUIInteraction failed:", e);
      return false;
    }
  }, []);

  /**
   * Send a string to UE.
   *
   * Returns true if it went out on an open data channel, false if it was deferred
   * (or dropped, with `queue: false`). Callers that care can use the return value —
   * unlike the SDK, we never pretend a discarded message was delivered.
   *
   * Pass `{ queue: false }` for messages that are worthless once late — e.g.
   * CLOSED_BY_USER on unload, where the page is going away regardless.
   */
  const sendMessage = useCallback((msg, { queue = true } = {}) => {
    if (channelOpenRef.current) return emitNow(msg);

    if (!queue) {
      // Still worth one attempt: the channel may be open without us having seen the
      // event yet (e.g. a listener registered late). The SDK drops it if not.
      emitNow(msg);
      return false;
    }

    const pending = outboundQueueRef.current;
    pending.push(msg);
    if (pending.length > MAX_QUEUED_MESSAGES) pending.shift();
    pushDiag("sendQueued", { msg: String(msg).slice(0, 40), depth: pending.length });
    onStageRef.current?.("send_queued", { depth: pending.length });
    return false;
  }, [emitNow, pushDiag]);

  const getStatus = useCallback(() => statusRef.current, []);

  /** Point-in-time view of every layer a message to UE has to cross. Diagnostics only. */
  const getConnectionHealth = useCallback(() => {
    const pc = streamRef.current?._webRtcController?.peerConnectionController?.peerConnection;
    return {
      status: statusRef.current,
      channelOpen: channelOpenRef.current,
      queued: outboundQueueRef.current.length,
      ice: pc?.iceConnectionState ?? null,
      pc: pc?.connectionState ?? null,
      hidden: document.hidden,
    };
  }, []);

  // Cancels an active AFK countdown — same as clicking the SDK's own overlay
  // (restarts the idle timer and resumes the video if it was paused).
  const dismissAfkWarning = useCallback(() => {
    try { dismissAfkRef.current?.(); } catch (_) {}
  }, []);

  return {
    containerRef,
    status,
    ready,
    // True once UE can actually receive what we send. `status === "streaming"` only
    // means video arrived; these are separate facts and diverge under high latency.
    channelOpen,
    initError,
    bootTraceRef,
    play,
    cancel: disconnect,
    stop: disconnect,
    sendMessage,
    disconnectEventsRef,
    statusHistoryRef,
    getStatus,
    getConnectionHealth,
    afkWarning,
    afkTimedOut,
    dismissAfkWarning,
    disconnectSnapshot,
    liveSnapshot,
    connectionMessages,
    debugForceDisconnect,
  };
}
